import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import { ConversionOfSharesForm } from "./conversionOfSharesDomain";
import { VBox } from "../../components-v2/Box";
import { MSelect } from "../../components-v2/Select";
import { ArticleSelectorQuestion, YesNoQuestion } from "../common/commonComponents";
import { useTranslation } from "../../stores/RootStoreContext";
import { SharesToConvertPerShareholderModel } from "../../shareholderRegister/transactions/conversionOfShares/conversionOfSharesDomain";
import { DataTable, TableColumn } from "../../components-v2/DataTable";
import { MTextInput } from "../../components-v2/TextInput";
import { LinkButton } from "../../components-v2/Button";
import { Dialog } from "../../components-v2/Dialog";
import { ChangeArticlesOfAssociation } from "../changeArticlesOfAssociation/changeArticlesOfAssociationComponents";
import { UpdateArticlesAutomatically } from "../increaseOfShareCapital/increaseOfShareCapitalComponents";
import { shareTypeLabel } from "../../common/share";

export const ConversionOfSharesView: FC<{ form: ConversionOfSharesForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  useEffect(() => {
    return form.init();
  }, [form]);

  return (
    <VBox gap={"md"}>
      <MSelect
        model={form.fromShareTypeModel}
        label={t("decisions.conversionOfShares.fromShareClass")}
        disabled={form.isProjectLocked}
      />
      <VBox alignItems={"start"}>
        <MSelect
          model={form.toShareTypeModel}
          label={t("decisions.conversionOfShares.toShareClass")}
          disabled={form.isProjectLocked}
          fullWidth
        />

        {form.showAddShareClassDialog && <AddShareClassDialog form={form} />}
        <LinkButton
          label={t("decisions.conversionOfShares.createShareClass")}
          icon={"plus"}
          onClick={() => form.setShowAddShareClassDialog(true)}
          disabled={form.isProjectLocked}
        />
      </VBox>
      <UpdateArticlesAutomatically
        updateArticlesAutomatically={form.updateArticlesAutomatically}
        isProjectLocked={form.isProjectLocked}
      />
      {form.isUpdateArticlesAutomatically && (
        <ArticleSelectorQuestion
          question={t("decisions.conversionOfShares.articleOfAssociationNumber")}
          model={form.articleOfAssociationNumber}
          isLocked={form.isProjectLocked}
          onChange={form.articleOfAssociationNumber.setSelected}
        />
      )}
      {form.isUpdateArticlesManually && (
        <ChangeArticlesOfAssociation model={form.articleChangeModel} />
      )}
      <YesNoQuestion
        question={t("decisions.conversionOfShares.convertAllShares")}
        field={form.convertAllShares}
      />
      {!form.convertAllShares.value && (
        <VBox gap={"md"}>
          <strong>{t("decisions.conversionOfShares.sharesPerShareholder")}</strong>
          <SharesPerShareholderTable form={form} />
        </VBox>
      )}
    </VBox>
  );
});

function AddShareClassDialog({ form }: { form: ConversionOfSharesForm }) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("decisions.conversionOfShares.createShareClass")}
      onSubmit={() => form.addNewShareType()}
      onCancel={() => form.setShowAddShareClassDialog(false)}
    >
      <MSelect
        model={form.createShareTypeModel}
        label={t("decisions.conversionOfShares.newShareClass")}
        disabled={form.isProjectLocked}
      />
    </Dialog>
  );
}

const SharesPerShareholderTable: FC<{ form: ConversionOfSharesForm }> = observer(
  function SharesPerShareholderTable({ form }) {
    const { t } = useTranslation();
    const columns: TableColumn<SharesToConvertPerShareholderModel>[] = [
      {
        name: "shareholder.name",
        label: t("decisions.conversionOfShares.shareholder"),
        align: "left",
        width: "50%",
        getValue: (p) => <div>{p.shareholder.name}</div>,
      },
      {
        name: "numberOfShares",
        label: t(shareTypeLabel(form.fromShareTypeModel.value?.sharetypeCode)),
        align: "right",
        getValue: (p) => (
          <div>{p.shareholder.calculatedShares[form.fromShareTypeModel.value.sharetypeCode]}</div>
        ),
      },
      {
        name: "numberOfSharesToConvert",
        label: t("decisions.conversionOfShares.numberOfShares"),
        align: "left",
        notSorted: true,
        getValue: (shareholderModel) => (
          <MTextInput
            name={`shareholder.${shareholderModel.shareholder.name}.number`}
            model={shareholderModel.numberOfShares}
            onBlur={() => {
              shareholderModel.numberOfShares.commit();
              shareholderModel.validate(form.fromShareTypeModel.value);
            }}
            disabled={form.isProjectLocked}
          />
        ),
      },
    ];
    return (
      <DataTable
        variant={"EDITABLE"}
        tableColumns={columns}
        tableData={form.sharesPerShareholder}
        getId={(row) => row.id}
      />
    );
  },
);
